import type React from 'react';

import { Image } from '@change/ds-core-ui';
import { Grid } from '@change/ds-layout';
import { cn } from '@change/ds-utils';

import { PageHeaderTitle } from './components/PageHeaderTitle';
import { PageHeaderToolbar } from './components/PageHeaderToolbar';
import { usePageHeader } from './hooks/usePageHeader';
import { container as containerStyles, header as headerStyles } from './PageHeader.styles';

type PageHeaderProps = {
	/**
	 * When true, hides the sticky header on desktop (lg and xl breakpoints).
	 * Used to hide the sticky header when the inline header is visible in PageBody.
	 */
	hideOnDesktop?: boolean;
};

export const PageHeader: React.FC<PageHeaderProps> = ({ hideOnDesktop = false }) => {
	const {
		data: { mobile, orientation, actionButtons, buttonVariant, imageUrl, title },
	} = usePageHeader();

	const headerStyle = headerStyles({ isMobile: mobile, orientation });
	const containerStyle = containerStyles({ isMobile: mobile, orientation });

	// Conditionally hide on desktop when inline header is visible
	const visibilityClasses = hideOnDesktop ? 'lg:hidden' : '';

	return (
		<section
			className={cn(
				'border-stroke-weaker bg-background-base z-20 w-full border-b md:sticky md:top-0 md:right-0 md:left-0',
				visibilityClasses,
			)}
		>
			<Grid className="px-0! py-0!">
				<header className={headerStyle} aria-label="Starter Dashboard page header" data-testid="page-header">
					<div data-testid="page-header-container" className={containerStyle}>
						<PageHeaderTitle
							image={
								imageUrl && (
									<Image
										alt={title}
										data-testid="starter-dashboard-header-image"
										data-qa="starter-dashboard-header-image"
										src={imageUrl}
										className="rounded-medium flex aspect-square h-[40px] w-[40px] shrink-0 items-center justify-center gap-3.5 object-cover"
									/>
								)
							}
							title={title}
						/>
						{actionButtons.length > 0 && (
							<PageHeaderToolbar actionButtons={actionButtons} mobile={mobile} buttonVariant={buttonVariant} />
						)}
					</div>
				</header>
			</Grid>
		</section>
	);
};
